import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import useAuthStore from "@/stores/user/authStore";
import useEditProfileModalStore from "@/stores/ui/editProfileModalStore";
import CustomDropdown from "@/components/ui/CustomDropdown";
import {
  Cog6ToothIcon,
  LanguageIcon,
  PencilSquareIcon,
  HeartIcon,
  BookmarkIcon,
} from "@heroicons/react/24/outline";
import { CircleUserRound, ArrowRightCircle } from "lucide-react";
import { AvatarImage } from "@/components/ui/DefaultAvatar";

const UserProfileLodge: React.FC = () => {
  const { user: currentUser, profile_image, anonymousUser } = useAuthStore();
  const { openModal } = useEditProfileModalStore();
  const [isOpen, setIsOpen] = useState(false);
  const handleLogout = async () => {
    const response = await axios.post("api/auth/logout");
    if (response.status === 200) {
      window.location.href = "/";
    } else {
      alert("Error logging out");
    }
  };

  const navigate = useNavigate();

  const cdnUrl = import.meta.env.VITE_CDN_URL as string;
  const defaultAvatar = cdnUrl + "user/defaultUser.png";

  return (
    <div className="relative flex flex-row items-center">
      {currentUser && !anonymousUser ? (
        <CustomDropdown
          title="User Profile"
          trigger={
            <button className="relative h-10 w-10 2xl:h-11 2xl:w-11 flex items-center justify-center bg-white/90 backdrop-blur-sm border border-neutral-200 rounded-full hover:bg-neutral-50 transition-colors duration-200 shadow-sm focus:outline-none">
              <AvatarImage
                src={profile_image || defaultAvatar}
                alt={`${currentUser.first_name}'s profile`}
                firstName={currentUser.first_name}
                lastName={currentUser.last_name}
                className="rounded-full w-6 h-6 2xl:w-[28px] 2xl:h-[28px] object-cover"
              />
            </button>
          }
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          className="w-64 bg-white space-y-1 p-3"
        >
          <div className="pb-2 mb-2 border-b border-neutral-100">
            <div className="flex items-center space-x-3 px-2 py-1">
              <AvatarImage
                src={profile_image || defaultAvatar}
                alt={`${currentUser.first_name}'s profile`}
                firstName={currentUser.first_name}
                lastName={currentUser.last_name}
                className="rounded-full w-10 h-10 2xl:w-[22px] 2xl:h-[22px] object-cover"
              />
              <div className="flex flex-col">
                <span className="font-medium">{currentUser.first_name}</span>
                <button
                  onClick={() => navigate("/account")}
                  className="font-light text-sm text-neutral-500 hover:text-indigo-700 duration-300 ease-in-out "
                >
                  View Profile
                </button>
              </div>
            </div>
          </div>

          <Link
            to="/account"
            className="flex flex-row items-center px-2 py-2.5 cursor-pointer rounded-lg hover:bg-neutral-100 transition duration-300 ease-in-out"
          >
            <CircleUserRound
              className="h-5 w-5 text-neutral-600"
              strokeWidth={1.5}
            />
            <span className="ml-3 text-sm font-light">Profile</span>
          </Link>

          <div className="border-t border-neutral-100 my-2"></div>

          <div
            onClick={() => {
              openModal();
              setIsOpen(false);
            }}
            className="flex flex-row items-center px-2 py-2.5 cursor-pointer rounded-lg hover:bg-neutral-100 transition duration-300 ease-in-out"
          >
            <Cog6ToothIcon className="h-5 w-5 text-neutral-600" />
            <span className="ml-3 text-sm font-light">Settings</span>
          </div>

          {/* <div className="flex flex-row items-center px-2 py-2.5 cursor-pointer rounded-lg hover:bg-neutral-100 transition duration-300 ease-in-out">
            <LanguageIcon className="h-5 w-5 text-neutral-600" />
            <span className="ml-3 text-sm font-light">Language</span>
          </div> */}

          <Link
            to="/contact"
            className="flex flex-row items-center px-2 py-2.5 cursor-pointer rounded-lg hover:bg-neutral-100 transition duration-300 ease-in-out"
          >
            <PencilSquareIcon className="h-5 w-5 text-neutral-600" />
            <span className="ml-3 text-sm font-light">Contact</span>
          </Link>

          <div className="border-t border-neutral-100 my-2"></div>

          <div
            onClick={handleLogout}
            className="flex flex-row items-center px-2 py-2.5 cursor-pointer rounded-lg hover:bg-neutral-100 transition duration-300 ease-in-out text-rose-500"
          >
            <ArrowRightCircle className="h-5 w-5" strokeWidth={1.5} />
            <span className="ml-3 text-sm font-normal">Sign Out</span>
          </div>
        </CustomDropdown>
      ) : (
        <Link
          to="/login"
          className="flex items-center justify-center px-4 h-10 font-normal text-sm rounded-full bg-white/90 backdrop-blur-sm border border-neutral-200 hover:bg-neutral-50 transition-colors duration-200 shadow-sm focus:outline-none"
        >
          <CircleUserRound
            className="w-5 h-5 text-neutral-600"
            strokeWidth={1.5}
          />
          <span className="ml-2">Get Started</span>
        </Link>
      )}
    </div>
  );
};

export default UserProfileLodge;
